import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useSelector } from "react-redux";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import apiClient from "../services/apiClient";
import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";
import KpiCard from "../components/Reports/KpiCard";
import { formatDate, formatToSeconds } from "../utils/utils";
import toast from "react-hot-toast";

const PAGE_LIMIT = 500;
const MAX_PAGES = 50;

const DIRECTION_COLORS = ["#4C4A8D", "#9d94ed"];
const STATUS_COLORS = {
  Lead: { bg: "#E6F4EA", color: "#1E7E34" },
  Contacted: { bg: "#FFF4E0", color: "#B26A00" },
  "Needs Callback": { bg: "#FDECEA", color: "#C62828" },
};

const classifyOutboundCall = (billseconds) => {
  if (billseconds === 0) return "Needs Callback";
  if (billseconds > 60) return "Lead";
  return "Contacted";
};

// Internal extension-to-extension dials (short numeric destinations) aren't real
// customer contact attempts, so they're excluded from lead/callback classification.
const isExternalNumber = (number) => /^\+?\d{7,}$/.test((number || "").toString().trim());

const toDateInputValue = (date) => date.toISOString().split("T")[0];

const buildDefaultFilters = () => {
  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);

  return {
    call_direction: "",
    extension: "",
    start_date: toDateInputValue(tenDaysAgo),
    end_date: toDateInputValue(today),
  };
};

const Reports = () => {
  const userInfo = useSelector((state) => state.userInfo.user);
  const isAdmin = userInfo?.role === "2";

  const [loading, setLoading] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [isTruncated, setIsTruncated] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(buildDefaultFilters);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchTeamOptions = async () => {
      try {
        const response = await apiClient.get(`/UsersByCompany/${userInfo?.company_id}`);
        const users = response?.data?.users || [];
        const options = users
          .map((u) => ({ label: u?.name, value: u?.extensions?.[0]?.data?.username }))
          .filter((opt) => opt.value);
        setTeamOptions(options);
      } catch (err) {
        console.log(err);
      }
    };

    fetchTeamOptions();
  }, [isAdmin, userInfo?.company_id]);

  useEffect(() => {
    fetchAllCallLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const fetchAllCallLogs = async () => {
    setLoading(true);
    try {
      let allLogs = [];
      let page = 1;
      let reachedCap = true;

      while (page <= MAX_PAGES) {
        const payload = {
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
          call_direction: appliedFilters.call_direction,
          callerid: "",
          callednum: "",
          callerid_and_or: "",
          callednum_and_or: "and",
          forward_to: "",
          page,
          limit: PAGE_LIMIT,
          OrgId: userInfo?.company_domain,
        };

        if (userInfo?.role === "3") {
          if (!userInfo?.settings?.default_extension?.extension) {
            reachedCap = false;
            break;
          }
          payload.extension = userInfo?.settings?.default_extension?.extension;
        } else if (appliedFilters.extension) {
          payload.extension = appliedFilters.extension;
        }

        const response = await apiClient.post(
          `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls`,
          payload
        );
        const logs = response?.data || [];
        allLogs = [...allLogs, ...logs];

        if (logs.length < PAGE_LIMIT) {
          reachedCap = false;
          break;
        }
        page += 1;
      }

      setCallLogs(allLogs);
      setIsTruncated(reachedCap);
    } catch (err) {
      console.log(err);
      toast.error("Network Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDraftChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const enrichedLogs = useMemo(
    () =>
      callLogs.map((log) => {
        const isOutbound = log?.call_direction === "outbound";
        const isExternal = isExternalNumber(log?.callednum);
        return {
          ...log,
          isInternal: isOutbound && !isExternal,
          status: isOutbound && isExternal ? classifyOutboundCall(log?.billseconds || 0) : null,
        };
      }),
    [callLogs]
  );

  const stats = useMemo(() => {
    const outboundLogs = enrichedLogs.filter((l) => l?.call_direction === "outbound");
    const inboundLogs = enrichedLogs.filter((l) => l?.call_direction === "inbound");
    const externalOutboundLogs = outboundLogs.filter((l) => !l.isInternal);
    const internalOutboundLogs = outboundLogs.filter((l) => l.isInternal);

    const leads = externalOutboundLogs.filter((l) => l.status === "Lead").length;
    const needsCallback = externalOutboundLogs.filter((l) => l.status === "Needs Callback").length;
    const contacted = externalOutboundLogs.filter((l) => l.status === "Contacted").length;

    const inboundAnswered = inboundLogs.filter((l) => (l?.billseconds || 0) > 0).length;
    const inboundMissed = inboundLogs.length - inboundAnswered;

    const totalOutboundDuration = externalOutboundLogs.reduce((sum, l) => sum + (l?.billseconds || 0), 0);
    const avgOutboundDuration = externalOutboundLogs.length
      ? Math.round(totalOutboundDuration / externalOutboundLogs.length)
      : 0;

    return {
      totalCalls: enrichedLogs.length,
      totalOutbound: outboundLogs.length,
      totalInbound: inboundLogs.length,
      externalOutbound: externalOutboundLogs.length,
      internalOutbound: internalOutboundLogs.length,
      leads,
      needsCallback,
      contacted,
      inboundAnswered,
      inboundMissed,
      avgOutboundDuration,
      leadConversionRate: externalOutboundLogs.length
        ? Math.round((leads / externalOutboundLogs.length) * 100)
        : 0,
    };
  }, [enrichedLogs]);

  const directionData = useMemo(
    () => [
      { name: "Inbound", value: stats.totalInbound },
      { name: "Outbound", value: stats.totalOutbound },
    ],
    [stats.totalInbound, stats.totalOutbound]
  );

  const outcomeData = useMemo(
    () => [
      { name: "Lead", value: stats.leads, color: STATUS_COLORS.Lead.color },
      { name: "Contacted", value: stats.contacted, color: STATUS_COLORS.Contacted.color },
      { name: "Needs Callback", value: stats.needsCallback, color: STATUS_COLORS["Needs Callback"].color },
    ],
    [stats.leads, stats.contacted, stats.needsCallback]
  );

  const dailyTrend = useMemo(() => {
    const map = {};
    enrichedLogs.forEach((log) => {
      if (!log?.callstart) return;
      const date = new Date(log.callstart + " GMT");
      if (Number.isNaN(date.getTime())) return;
      const key = date.toLocaleDateString("en-CA");
      if (!map[key]) map[key] = { date: key, inbound: 0, outbound: 0 };
      if (log.call_direction === "inbound") map[key].inbound += 1;
      else if (log.call_direction === "outbound") map[key].outbound += 1;
    });
    return Object.values(map).sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [enrichedLogs]);

  const columns = useMemo(
    () => [
      { accessorKey: "call_direction", header: "Type" },
      { accessorKey: "callerid", header: "Caller no." },
      { accessorKey: "callednum", header: "Receiver no." },
      {
        accessorKey: "billseconds",
        header: "Duration",
        Cell: ({ cell }) => formatToSeconds(cell.getValue() || 0),
      },
      {
        accessorKey: "callstart",
        header: "Date",
        Cell: ({ cell }) => formatDate(cell.getValue() + " GMT"),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => {
          const status = row.original?.status;
          if (row.original?.isInternal) {
            return (
              <Chip label="Internal" size="small" sx={{ backgroundColor: "#EEEEEE", color: "#666" }} />
            );
          }
          if (!status) return "-";
          const colors = STATUS_COLORS[status] || {};
          return (
            <Chip
              label={status}
              size="small"
              sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }}
            />
          );
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: enrichedLogs,
    enableColumnOrdering: true,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enablePagination: true,
    initialState: { pagination: { pageSize: 10, pageIndex: 0 } },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
    },
    muiTableHeadCellProps: {
      sx: { backgroundColor: "#F5F6F8" },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: "12px", border: "1px solid #e0e0e0" },
    },
  });

  return (
    <>
      {loading && <PageLoader text="Loading Reports" />}

      <div className="pageTitle1 p-5">
        <h3>Reports</h3>
        <p>{stats.totalCalls} calls analyzed</p>
      </div>

      <Box sx={{ padding: 2 }}>
        {isTruncated && (
          <Box
            sx={{
              backgroundColor: "#FFF4E0",
              border: "1px solid #F0B429",
              borderRadius: "8px",
              padding: 1.5,
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: "#8a6100" }}>
              Showing the first {stats.totalCalls} calls for this range — narrow the date range or
              filters to see a complete report.
            </Typography>
          </Box>
        )}

        <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
          <Autocomplete
            disablePortal
            options={["inbound", "outbound"]}
            value={draftFilters.call_direction || null}
            onChange={(event, newValue) => {
              setDraftFilters((prev) => ({ ...prev, call_direction: newValue || "" }));
            }}
            sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
            size="small"
            renderInput={(params) => <TextField {...params} label="Type" />}
          />

          {isAdmin && (
            <Autocomplete
              disablePortal
              options={teamOptions}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={teamOptions.find((opt) => opt.value === draftFilters.extension) || null}
              onChange={(event, newValue) => {
                setDraftFilters((prev) => ({ ...prev, extension: newValue?.value || "" }));
              }}
              sx={{ width: 220, backgroundColor: "white", borderRadius: "8px" }}
              size="small"
              renderInput={(params) => <TextField {...params} label="Team Member" />}
            />
          )}

          <CustomInputBox
            label="Start Date"
            name="start_date"
            type="date"
            value={draftFilters.start_date}
            onChange={handleDraftChange}
            width="180px"
            margin="0"
            backgroundColor={"#FFFFFF"}
            borderRadius="8px"
            InputLabelProps={{ shrink: true }}
          />

          <CustomInputBox
            label="End Date"
            name="end_date"
            type="date"
            value={draftFilters.end_date}
            onChange={handleDraftChange}
            width="180px"
            margin="0"
            backgroundColor={"#FFFFFF"}
            borderRadius="8px"
            InputLabelProps={{ shrink: true }}
          />

          <CustomButton
            text={"Apply Filters"}
            onClick={handleApplyFilters}
            loading={loading}
            disabled={loading}
          />
        </div>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4 }}>
          <KpiCard title="Total Calls" value={stats.totalCalls} />
          <KpiCard title="Outbound Calls" value={stats.totalOutbound} />
          <KpiCard title="Inbound Calls" value={stats.totalInbound} />
          <KpiCard
            title="Leads"
            value={stats.leads}
            subtext={`${stats.leadConversionRate}% of external outbound`}
            color={STATUS_COLORS.Lead.color}
          />
          <KpiCard title="Needs Callback" value={stats.needsCallback} color={STATUS_COLORS["Needs Callback"].color} />
          <KpiCard title="Contacted" value={stats.contacted} color={STATUS_COLORS.Contacted.color} />
          <KpiCard title="Avg. Outbound Duration" value={formatToSeconds(stats.avgOutboundDuration)} subtext="External calls only" />
          <KpiCard
            title="Inbound Answered / Missed"
            value={`${stats.inboundAnswered} / ${stats.inboundMissed}`}
            subtext="Derived from call duration"
          />
          <KpiCard
            title="Internal Calls Excluded"
            value={stats.internalOutbound}
            subtext="Extension-to-extension, not counted as leads"
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4 }}>
          <Box sx={{ flex: "1 1 320px", minWidth: 320, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", padding: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Inbound vs Outbound
            </Typography>
            {stats.totalCalls ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={directionData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {directionData.map((entry, idx) => (
                      <Cell key={entry.name} fill={DIRECTION_COLORS[idx]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" sx={{ color: "#999" }}>
                No calls in this range
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: "1 1 320px", minWidth: 320, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", padding: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Outbound Outcomes
            </Typography>
            <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 1 }}>
              External calls only — excludes {stats.internalOutbound} internal extension dials
            </Typography>
            {stats.externalOutbound ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={outcomeData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {outcomeData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" sx={{ color: "#999" }}>
                No outbound calls in this range
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: "1 1 100%", backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", padding: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Calls Per Day
            </Typography>
            {dailyTrend.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="inbound" stackId="calls" fill={DIRECTION_COLORS[0]} />
                  <Bar dataKey="outbound" stackId="calls" fill={DIRECTION_COLORS[1]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" sx={{ color: "#999" }}>
                No calls in this range
              </Typography>
            )}
          </Box>
        </Box>

        <MaterialReactTable table={table} />
      </Box>
    </>
  );
};

export default Reports;
